import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { getToolTypeLabel } from '../toolLabels';

const AttachCalculationsDialog = ({ open, onClose, projectId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/api/v1/project/calculations');
      const data = res?.data?.data?.data || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load saved calculations.';
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const visibleItems = useMemo(() => {
    const pid = String(projectId || '');
    return items.filter((i) => String(i?.project?._id || i?.project || '') !== pid);
  }, [items, projectId]);

  const selectedItems = useMemo(() => visibleItems.filter((i) => selectedIds.has(i._id)), [visibleItems, selectedIds]);

  const groupedItems = useMemo(() => {
    const groups = new Map();
    visibleItems.forEach((item) => {
      const key = item?.toolKey || 'Unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return Array.from(groups.entries()).map(([toolKey, groupItems]) => ({
      toolKey,
      label: getToolTypeLabel(toolKey),
      items: groupItems,
    }));
  }, [visibleItems]);

  const handleAttach = async () => {
    if (selectedItems.length === 0 || !projectId) return;
    try {
      setSubmitting(true);
      setError('');
      await axios.post(`/api/v1/project/${projectId}/attach-calc`, {
        items: selectedItems.map((item) => ({ toolKey: item.toolKey, calcId: item.calcId })),
      });
      await fetchAll();
      setSelectedIds(new Set());
      onApplied?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to attach calculations.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderAttachedLabel = (item) => {
    const rawProject = item?.project;
    if (!rawProject) return 'Unassigned';
    return rawProject?.name || 'Assigned';
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1)}…`;
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 3.7 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Attach calculations</Typography>
          <IconButton onClick={onClose} disabled={submitting} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : visibleItems.length === 0 ? (
          <Typography color="text.secondary">No calculations available to attach.</Typography>
        ) : (
          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            {groupedItems.map((group) => (
              <Box key={group.toolKey} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ px: 1, py: 0.5, fontWeight: 700 }}>
                  {group.label}
                </Typography>
                <Box>
                  {group.items.map((item) => {
                    const name = item?.calcName || '(unnamed calculation)';
                    const attachedLabel = renderAttachedLabel(item);
                    const truncatedName = truncateText(name, 40);
                    const truncatedProject = truncateText(attachedLabel, 30);

                    return (
                      <Box
                        key={item._id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 220px 48px',
                          gap: 1,
                          px: 1,
                          py: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Tooltip title={name} placement="top" arrow>
                            <Typography variant="body2">{truncatedName}</Typography>
                          </Tooltip>
                        </Box>
                        <Box>
                          <Tooltip title={attachedLabel} placement="top" arrow>
                            <Chip
                              size="small"
                              color={item?.project ? 'warning' : 'success'}
                              label={truncatedProject}
                              variant="filled"
                              sx={{ maxWidth: 200 }}
                            />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            edge="end"
                            checked={selectedIds.has(item._id)}
                            onChange={() => toggleSelect(item._id)}
                            disabled={submitting}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!!error && (
          <Box sx={{ mt: 1 }} color="error.main">
            {error}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleAttach} disabled={submitting || selectedIds.size === 0}>
          Attach or move to this project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachCalculationsDialog;
